/**
 * Team Hours Table Component
 * Shows planned vs actual hours by team member with financial details
 */

import { useMemo } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import type { TeamHoursResponse } from '@/types/ui/dashboard'
import { cn } from '@/lib/utils'
import { useFormatters } from '@/hooks/use-formatters'
import { ChartExportMenu } from './ChartExportMenu'
import { exportToCSV, exportToExcel, exportToPNG, generateFilename } from '@/lib/utils/export-utils'

interface TeamHoursTableProps {
  data: TeamHoursResponse | null
  isLoading?: boolean
  engagementId?: string | number
}

interface TotalsRow {
  name: string
  planned: number
  actual: number
  variance: number
  variancePercent: number
  billable: number
  billablePercent: number
  nonBillable: number
  nonBillablePercent: number
}

export function TeamHoursTable({ data, isLoading, engagementId = 0 }: TeamHoursTableProps) {
  const { formatDecimal, formatPercent } = useFormatters()

  // Calculate totals row from team members
  const enrichedData = useMemo(() => {
    if (!data || data.teamMembers.length === 0) return null

    // Calculate all totals by summing across team members
    const totalPlanned = data.teamMembers.reduce((sum, m) => sum + m.planned, 0)
    const totalActual = data.teamMembers.reduce((sum, m) => sum + m.actual, 0)
    const totalVariance = totalActual - totalPlanned
    const totalVariancePercent = totalPlanned > 0 ? (totalVariance / totalPlanned) * 100 : 0
    const totalBillable = data.teamMembers.reduce((sum, m) => sum + m.billable, 0)
    const totalBillablePercent = totalActual > 0 ? (totalBillable / totalActual) * 100 : 0
    const totalNonBillable = data.teamMembers.reduce((sum, m) => sum + m.nonBillable, 0)
    const totalNonBillablePercent = totalActual > 0 ? (totalNonBillable / totalActual) * 100 : 0

    const totals: TotalsRow = {
      name: 'TOTAL',
      planned: totalPlanned,
      actual: totalActual,
      variance: totalVariance,
      variancePercent: totalVariancePercent,
      billable: totalBillable,
      billablePercent: totalBillablePercent,
      nonBillable: totalNonBillable,
      nonBillablePercent: totalNonBillablePercent,
    }

    return {
      teamMembers: data.teamMembers,
      totals,
    }
  }, [data])

  // Build export-ready data from enrichedData
  const buildExportData = () => {
    if (!enrichedData) return []
    return [
      ...enrichedData.teamMembers.map(member => ({
        Name: member.name,
        Status: member.status,
        Planned: member.planned,
        Actual: member.actual,
        'Variance %': member.variancePercent,
        Billable: member.billable,
        'Billable %': member.billablePercent,
        'Non-Billable': member.nonBillable,
        'Non-Billable %': member.nonBillablePercent,
      })),
      {
        Name: enrichedData.totals.name,
        Status: '',
        Planned: enrichedData.totals.planned,
        Actual: enrichedData.totals.actual,
        'Variance %': enrichedData.totals.variancePercent,
        Billable: enrichedData.totals.billable,
        'Billable %': enrichedData.totals.billablePercent,
        'Non-Billable': enrichedData.totals.nonBillable,
        'Non-Billable %': enrichedData.totals.nonBillablePercent,
      }
    ]
  }

  // Export handlers
  const handleExportCSV = () => {
    if (!enrichedData) return
    exportToCSV(buildExportData(), generateFilename('TeamHours', engagementId, 'csv'))
  }

  const handleExportExcel = () => {
    if (!enrichedData) return
    exportToExcel(buildExportData(), generateFilename('TeamHours', engagementId, 'xlsx'), 'Team Hours')
  }

  const handleExportPNG = () => {
    exportToPNG('team-hours-table', generateFilename('TeamHours', engagementId, 'png'))
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Team Hours Summary</CardTitle>
          <CardDescription>Planned vs Actual hours by team member</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!enrichedData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Team Hours Summary</CardTitle>
          <CardDescription>Planned vs Actual hours by team member</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-8">
            No team hours data available
          </p>
        </CardContent>
      </Card>
    )
  }

  const getVarianceColor = (variance: number) => {
    if (variance > 0) return 'text-green-600'
    if (variance < 0) return 'text-red-600'
    return ''
  }

  // Worker status pill colors (Active | Completed | Terminated | Removed)
  const getStatusClass = (status: string) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-700'
      case 'Completed': return 'bg-blue-100 text-blue-700'
      case 'Terminated': return 'bg-red-100 text-red-700'
      case 'Removed': return 'bg-gray-200 text-gray-600'
      default: return 'bg-gray-100 text-gray-600'
    }
  }

  return (
    <Card id="team-hours-table">
      <CardHeader className="py-3 px-4">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>Team Hours Summary</CardTitle>
            <CardDescription>Planned vs Actual hours by team member</CardDescription>
          </div>
          <ChartExportMenu 
            onExportCSV={handleExportCSV}
            onExportExcel={handleExportExcel}
            onExportPNG={handleExportPNG}
          />
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b text-left">
                <th className="py-3 px-3 text-xs font-medium text-muted-foreground">Name</th>
                <th className="py-3 px-3 text-xs font-medium text-muted-foreground">Status</th>
                <th className="py-3 px-3 text-xs font-medium text-muted-foreground text-right">Planned</th>
                <th className="py-3 px-3 text-xs font-medium text-muted-foreground text-right">Actual</th>
                <th className="py-3 px-3 text-xs font-medium text-muted-foreground text-right">Variance</th>
                <th className="py-3 px-3 text-xs font-medium text-muted-foreground text-right">Billable</th>
                <th className="py-3 px-3 text-xs font-medium text-muted-foreground text-right">Billable %</th>
                <th className="py-3 px-3 text-xs font-medium text-muted-foreground text-right">Non-Billable</th>
                <th className="py-3 px-3 text-xs font-medium text-muted-foreground text-right">Non-Billable %</th>
              </tr>
            </thead>
            <tbody>
              {enrichedData.teamMembers.map((member, index) => (
                <tr key={index} className="border-b hover:bg-muted/50 transition-colors">
                  <td className="py-3 px-3 text-sm">{member.name}</td>
                  <td className="py-3 px-3 text-sm">
                    <span className={cn('inline-block rounded px-2 py-0.5 text-xs font-medium', getStatusClass(member.status))}>
                      {member.status}
                    </span>
                  </td>
                  <td className="py-3 px-3 text-sm text-right tabular-nums">{formatDecimal(member.planned, 2)}</td>
                  <td className="py-3 px-3 text-sm text-right tabular-nums">{formatDecimal(member.actual, 2)}</td>
                  <td className={cn("py-3 px-3 text-sm text-right tabular-nums", getVarianceColor(member.variancePercent))}>
                    {formatPercent(member.variancePercent, 1)}
                  </td>
                  <td className="py-3 px-3 text-sm text-right tabular-nums">{formatDecimal(member.billable, 2)}</td>
                  <td className="py-3 px-3 text-sm text-right tabular-nums">{formatPercent(member.billablePercent, 1)}</td>
                  <td className="py-3 px-3 text-sm text-right tabular-nums">{formatDecimal(member.nonBillable, 2)}</td>
                  <td className="py-3 px-3 text-sm text-right tabular-nums">{formatPercent(member.nonBillablePercent, 1)}</td>
                </tr>
              ))}
              {/* Totals Row */}
              <tr className="bg-muted/50 font-semibold">
                <td className="py-3 px-3 text-sm">{enrichedData.totals.name}</td>
                <td className="py-3 px-3 text-sm"></td>
                <td className="py-3 px-3 text-sm text-right tabular-nums">{formatDecimal(enrichedData.totals.planned, 2)}</td>
                <td className="py-3 px-3 text-sm text-right tabular-nums">{formatDecimal(enrichedData.totals.actual, 2)}</td>
                <td className={cn("py-3 px-3 text-sm text-right tabular-nums", getVarianceColor(enrichedData.totals.variancePercent))}>
                  {formatPercent(enrichedData.totals.variancePercent, 1)}
                </td>
                <td className="py-3 px-3 text-sm text-right tabular-nums">{formatDecimal(enrichedData.totals.billable, 2)}</td>
                <td className="py-3 px-3 text-sm text-right tabular-nums">{formatPercent(enrichedData.totals.billablePercent, 1)}</td>
                <td className="py-3 px-3 text-sm text-right tabular-nums">{formatDecimal(enrichedData.totals.nonBillable, 2)}</td>
                <td className="py-3 px-3 text-sm text-right tabular-nums">{formatPercent(enrichedData.totals.nonBillablePercent, 1)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}

